import { z } from 'zod';
import { GigId } from '../ids.js';
import {
  PublicCode,
  Slug,
  IsoTimestamp,
  IanaTimezone,
  Version,
  Money,
  SafeLink,
} from '../primitives.js';
import { GigStatus, SetlistVisibilityMode, AudienceMode, PublicStatus } from '../enums.js';
import { ThemeTokens, PublicLink } from './tenant.js';
import { PublicLayout } from './gig.js';

/**
 * §15.4 Public snapshot payload — a materialized, sanitized document. This is a security
 * boundary (§33): it must contain NO workspace membership, private notes, draft content,
 * payment account identifiers, ntfy topic/credentials, moderation notes, audience identifiers,
 * internal keys, or exact tip records. Leakage tests assert this shape stays audience-safe.
 */

export const PublicArtistProjection = z.object({
  publicName: z.string(),
  pronunciation: z.string().optional(),
  tagline: z.string().optional(),
  shortBio: z.string().optional(),
  profileImageUrl: SafeLink.optional(),
  logoUrl: SafeLink.optional(),
  socialLinks: z.array(PublicLink).default([]),
  streamingLinks: z.array(PublicLink).default([]),
});
export type PublicArtistProjection = z.infer<typeof PublicArtistProjection>;

/** Public set-list item projection — never carries performer notes/transposition/hardware. */
export const PublicSetlistItem = z.object({
  type: z.enum(['song', 'improv_slot', 'announcement', 'break', 'custom']),
  displayTitle: z.string(),
  originalArtist: z.string().optional(),
  status: z.enum(['upcoming', 'current', 'played']),
  requestedByAudience: z.boolean().default(false),
});
export type PublicSetlistItem = z.infer<typeof PublicSetlistItem>;

export const PublicSetlistProjection = z.object({
  visibilityMode: SetlistVisibilityMode,
  sectionNames: z.array(z.string()).default([]),
  current: PublicSetlistItem.nullable().default(null),
  upcoming: z.array(PublicSetlistItem).default([]),
  played: z.array(PublicSetlistItem).default([]),
});
export type PublicSetlistProjection = z.infer<typeof PublicSetlistProjection>;

export const PublicSongPoolEntry = z.object({
  songId: z.string(),
  title: z.string(),
  originalArtist: z.string().optional(),
  tags: z.array(z.string()).default([]),
  requestable: z.boolean(),
  onCooldown: z.boolean().default(false),
});
export type PublicSongPoolEntry = z.infer<typeof PublicSongPoolEntry>;

export const PublicBreakState = z.object({
  active: z.boolean(),
  heading: z.string().optional(),
  message: z.string().optional(),
  expectedReturnAt: IsoTimestamp.nullable().default(null),
  showCountdown: z.boolean().default(false),
});
export type PublicBreakState = z.infer<typeof PublicBreakState>;

/** Public projection of an improv profile — only what the audience form needs (§20.11). */
export const PublicImprovProfile = z.object({
  profileId: z.string(),
  profileVersion: Version,
  audienceMode: AudienceMode,
  advancedDrilldown: z.boolean(),
  fields: z
    .array(
      z.object({
        dimension: z.string(),
        kind: z.string(),
        label: z.string().optional(),
        helpText: z.string().optional(),
        choices: z.array(z.object({ value: z.string(), label: z.string() })).default([]),
        required: z.boolean().default(false),
      }),
    )
    .default([]),
});
export type PublicImprovProfile = z.infer<typeof PublicImprovProfile>;

export const PublicTipOptions = z.object({
  enabled: z.boolean().default(false),
  heading: z.string().optional(),
  thankYouText: z.string().optional(),
  presets: z.array(Money).default([]),
  allowCustom: z.boolean().default(true),
  externalLinks: z.array(PublicLink).default([]),
});
export type PublicTipOptions = z.infer<typeof PublicTipOptions>;

export const PublicNowPlaying = z.object({
  title: z.string().nullable().default(null),
  originalArtist: z.string().nullable().default(null),
  requestedByAudience: z.boolean().default(false),
  customMessage: z.string().nullable().default(null),
});
export type PublicNowPlaying = z.infer<typeof PublicNowPlaying>;

/** The full sanitized payload the audience page reads in one request (§15.4). */
export const PublicSnapshotPayload = z.object({
  gigStatus: GigStatus,
  liveStateVersion: Version,
  artist: PublicArtistProjection,
  venueLabel: z.string().optional(),
  scheduledStartAt: IsoTimestamp.nullable().default(null),
  scheduledEndAt: IsoTimestamp.nullable().default(null),
  timezone: IanaTimezone,
  layout: PublicLayout,
  /** Enabled feature configuration safe for the audience (already filtered server-side). */
  enabledModules: z.array(z.string()).default([]),
  nowPlaying: PublicNowPlaying.nullable().default(null),
  setlist: PublicSetlistProjection.nullable().default(null),
  breakState: PublicBreakState.nullable().default(null),
  songPool: z.array(PublicSongPoolEntry).default([]),
  improvProfile: PublicImprovProfile.nullable().default(null),
  tips: PublicTipOptions.nullable().default(null),
  theme: ThemeTokens,
  publicStatusEnabled: z.boolean().default(false),
  acknowledgementCopy: z.record(z.string(), z.string()).default({}),
});
export type PublicSnapshotPayload = z.infer<typeof PublicSnapshotPayload>;

/** §27.21 PublicSnapshot envelope. */
export const PublicSnapshot = z.object({
  gigId: GigId,
  publicCode: PublicCode,
  artistSlug: Slug,
  snapshotVersion: Version,
  etag: z.string(),
  gigStatus: GigStatus,
  generatedAt: IsoTimestamp,
  expiresAt: IsoTimestamp.nullable().default(null),
  payload: PublicSnapshotPayload,
});
export type PublicSnapshot = z.infer<typeof PublicSnapshot>;

/** Audience-visible status of the caller's own interaction (§15.5 / §18.7). */
export const MyInteractionStatus = z.object({
  interactionId: z.string(),
  interactionType: z.string(),
  publicStatus: PublicStatus,
  submittedAt: IsoTimestamp,
});
export type MyInteractionStatus = z.infer<typeof MyInteractionStatus>;
